/**
 * Collection pagination with optional two-phase availability loading.
 * For the New collection, available products are fetched and shown first;
 * sold-out products begin only after every available-product page.
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();

      this.sectionId = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id=' + this.sectionId + ']');
      this.animationsEnabled = document.body.classList.contains('animations-true') &&
        typeof gsap !== 'undefined' &&
        !this.classList.contains('pagination-type--infinite');
      this.pages = Number.parseInt(this.dataset.pages || '1', 10);
      this.i = 2;
      this.sequence = 1;
      this.preloadDistance = 1000;

      const searchParams = new URLSearchParams(document.location.search);
      this.soldOutLast = this.dataset.soldOutLast === 'true' && !searchParams.has('filter.v.availability');
    }

    connectedCallback() {
      if (!this.grid) return;

      if (this.soldOutLast) {
        this.initializeSoldOutLast();
        return;
      }

      if (this.pages <= 1) {
        this.finish();
        return;
      }

      this.startLoader();
    }

    startLoader() {
      if (this.soldOutLast && this.classList.contains('pagination-type--paginated')) {
        this.convertToLoadMore();
      }

      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
      }

      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
    }

    convertToLoadMore() {
      this.classList.remove('pagination-type--paginated');
      this.classList.add('pagination-type--loadmore');
      this.innerHTML = '<div class="row"><div class="small-12 columns"><button class="load-more button" aria-label="Load more products"><span>Load more products</span></button></div></div>';
      this.button = this.querySelector('.load-more');
    }

    async initializeSoldOutLast() {
      this.baseSearchParams = new URLSearchParams(document.location.search);
      this.baseSearchParams.delete('page');
      this.baseSearchParams.delete('section_id');
      this.baseSearchParams.delete('filter.v.availability');

      this.phase = 'available';
      this.phasePage = 1;
      this.setLoading(true);

      try {
        const result = await this.fetchPhasePage(this.phase, this.phasePage);
        this.phasePages = result.pages;
        this.replaceGrid(result.products, false);
        this.startLoader();
      } catch (error) {
        console.warn('Available-first collection loading could not start; using the standard collection order.', error);
        this.soldOutLast = false;
        this.startLoader();
      } finally {
        this.setLoading(false);
      }
    }

    phaseUrl(phase, page) {
      const params = new URLSearchParams(this.baseSearchParams);
      params.set('filter.v.availability', phase === 'available' ? '1' : '0');
      params.set('page', String(page));
      params.set('section_id', this.sectionId);
      return document.location.pathname + '?' + params.toString();
    }

    async fetchPhasePage(phase, page) {
      const url = this.phaseUrl(phase, page);
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Collection request failed with status ' + response.status);
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const grid = parsed.getElementById('product-grid');
      const pager = parsed.querySelector('pagination-theme');
      const pages = Number.parseInt(pager?.dataset.pages || '1', 10);
      const products = grid ? Array.from(grid.querySelectorAll('.products--column')) : [];

      return { url, pages, products };
    }

    replaceGrid(products, showSoldHeading) {
      const loadingOverlay = this.grid.querySelector(':scope > .loading-overlay');
      const children = [];

      if (showSoldHeading && products.length) {
        children.push(this.createSoldHeading());
      }

      children.push(...products);
      if (loadingOverlay) children.push(loadingOverlay);

      this.grid.replaceChildren(...children);
      this.activateProducts(products);
    }

    createSoldHeading() {
      const divider = document.createElement('div');
      divider.className = 'sold-out-last-divider';
      divider.style.gridColumn = '1 / -1';
      divider.style.margin = '3rem 0 1rem';
      divider.innerHTML = '<h2 style="margin:0">Sold items</h2>';
      return divider;
    }

    appendProducts(products, showSoldHeading = false) {
      const loadingOverlay = this.grid.querySelector(':scope > .loading-overlay');

      if (showSoldHeading && products.length && !this.grid.querySelector('.sold-out-last-divider')) {
        this.grid.insertBefore(this.createSoldHeading(), loadingOverlay);
      }

      this.sequence += 1;
      products.forEach((product) => {
        product.dataset.pagination = String(this.sequence);
        this.grid.insertBefore(product, loadingOverlay);
      });

      this.activateProducts(products);
    }

    activateProducts(products) {
      products.forEach((product) => {
        product.querySelectorAll('.lazyload').forEach((image) => {
          lazySizes.loader.unveil(image);
        });
      });

      if (this.animationsEnabled && products.length) {
        const images = products
          .map((product) => product.querySelector('.product-card--image-primary'))
          .filter(Boolean);

        gsap.to(images, {
          scale: 1,
          opacity: 1,
          stagger: 0.15,
          ease: window.theme.settings.animation_easing
        });
      } else if (typeof window.StampedFn !== 'undefined' && typeof window.StampedFn.loadBadges === 'function') {
        window.StampedFn.loadBadges();
      }
    }

    setLoading(isLoading) {
      this.toggleAttribute('loading', isLoading);

      // Keep infinite scroll visually stable. The pagination spinner still
      // communicates progress without dimming or repainting the whole grid.
      if (this.classList.contains('pagination-type--infinite')) {
        this.grid?.classList.remove('loading');
      } else {
        this.grid?.classList.toggle('loading', isLoading);
      }
    }

    finish() {
      if (this.observer) {
        this.observer.unobserve(this);
      }

      if (this.button) {
        this.button.classList.add('visually-hidden');
      }

      this.hidden = true;
      this.removeAttribute('loading');
    }

    addUrlParam(search, key) {
      const newParam = key + '=' + this.i;
      let params = '?' + newParam;

      if (search) {
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);
        if (params === search) params += '&' + newParam;
      }

      return params;
    }

    loadMore() {
      if (!this.button) return;

      this.button.addEventListener('click', () => {
        this.loadProducts();
        this.button.blur();
      });
    }

    infinite() {
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.loadProducts();
        }
      }, {
        // Start loading roughly one viewport before the visitor reaches the
        // end of the current product grid.
        rootMargin: `0px 0px ${this.preloadDistance}px 0px`,
        threshold: 0
      });

      this.observer.observe(this);
    }

    continueInfiniteIfNeeded() {
      if (!this.classList.contains('pagination-type--infinite') || this.hidden) return;

      // A short batch can leave the pagination marker inside the observer's
      // preload area. IntersectionObserver will not fire again until its
      // intersection state changes, so explicitly continue while it remains
      // close enough to the viewport.
      requestAnimationFrame(() => {
        if (this.hidden || this.hasAttribute('loading')) return;

        const bounds = this.getBoundingClientRect();
        const isInsidePreloadArea = bounds.top <= window.innerHeight + this.preloadDistance && bounds.bottom >= 0;

        if (isInsidePreloadArea) {
          this.loadProducts();
        }
      });
    }

    loadProducts() {
      if (this.soldOutLast) {
        this.loadSoldOutLastProducts();
        return;
      }

      if (this.hasAttribute('loading')) return;
      if (this.i > this.pages) {
        this.finish();
        return;
      }

      const page = this.i;
      const url = document.location.pathname + this.addUrlParam(document.location.search, 'page');
      this.i += 1;
      this.setLoading(true);

      fetch(url)
        .then((response) => response.text())
        .then((html) => {
          this.renderProducts(html);
          if (page >= this.pages) this.finish();

          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('pagination:page-changed', { url });
          }
        })
        .finally(() => {
          this.setLoading(false);
          this.continueInfiniteIfNeeded();
        });
    }

    async loadSoldOutLastProducts() {
      if (this.hasAttribute('loading')) return;

      let nextPhase = this.phase;
      let nextPage = this.phasePage + 1;
      let showSoldHeading = false;

      if (nextPage > this.phasePages) {
        if (this.phase === 'available') {
          nextPhase = 'sold';
          nextPage = 1;
          showSoldHeading = true;
        } else {
          this.finish();
          return;
        }
      }

      this.setLoading(true);

      try {
        const result = await this.fetchPhasePage(nextPhase, nextPage);

        if (!result.products.length) {
          if (nextPhase === 'available') {
            this.phase = 'available';
            this.phasePage = this.phasePages;
          } else {
            this.finish();
          }
          return;
        }

        this.phase = nextPhase;
        this.phasePage = nextPage;
        this.phasePages = result.pages;
        this.appendProducts(result.products, showSoldHeading);

        if (typeof dispatchCustomEvent === 'function') {
          dispatchCustomEvent('pagination:page-changed', { url: result.url });
        }
      } catch (error) {
        console.warn('The next collection page could not be loaded.', error);
      } finally {
        this.setLoading(false);
        this.continueInfiniteIfNeeded();
      }
    }

    renderProducts(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacementGrid = parsed.getElementById('product-grid');

      if (!replacementGrid) {
        this.finish();
        return;
      }

      const products = Array.from(replacementGrid.querySelectorAll('.products--column'));
      if (!products.length) {
        this.finish();
        return;
      }

      this.appendProducts(products);
    }
  }

  customElements.define('pagination-theme', Pagination);
}
